import React from 'react';
import { motion } from 'framer-motion';
import { Eye, LockKeyhole, Siren, Gauge, CheckCircle2, Users, Wifi, Zap, Clock3 } from 'lucide-react';
import { CTAButton, PageHero, SectionLabel } from '../components/shared';
export function Commercial() {
  return <>
      <PageHero eyebrow="Commercial security" title="Built for businesses that need more than a basic alarm." subtitle="Most properties collect security systems over time. Watchdog brings cameras, alarms, doors, and apps into one clean plan — designed around how your business actually operates." data-id="element-201">
        <div className="flex flex-wrap gap-3" data-id="element-202">
          <CTAButton to="/assessment" data-id="element-203">Free commercial assessment</CTAButton>
          <CTAButton variant="secondary" to="/services" data-id="element-204">
            See all solutions
          </CTAButton>
        </div>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-id="element-205">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-206">
          {([[Eye, 'Visibility', 'Live and recorded video for the areas that matter most — entrances, registers, kitchens, lots, and back doors.'], [LockKeyhole, 'Control', 'Doors, schedules, users, locks, and temporary access — all from one app, with a full audit trail.'], [Siren, 'Response', 'Smarter alerts when doors, sensors, or cameras detect activity outside of normal patterns.'], [Gauge, 'Operations', 'Security that also helps opening, closing, staff flow, vendor visits, and remote oversight.']] as const).map(([Icon, title, text], i) => <motion.div key={title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.06
        }} className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-id="element-207">
              <Icon className="mb-5 h-8 w-8 text-sky-500" data-id="element-208" />
              <h3 className="text-2xl font-black" data-id="element-209">{title}</h3>
              <p className="mt-3 leading-7 text-slate-600" data-id="element-210">{text}</p>
            </motion.div>)}
        </div>
      </section>

      <section className="bg-slate-950 py-20 text-white" data-id="element-211">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-212">
          <SectionLabel dark data-id="element-213">Why operators pick Watchdog</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-214">
            National technology. Local service.
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-215">
            {([[Users, 'Staff changes are easier'], [Wifi, 'Remote alerts stay connected'], [Zap, 'Faster awareness'], [Clock3, 'Cleaner opening and closing']] as const).map(([Icon, text]) => <div key={text} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-5" data-id="element-216">
                <Icon className="h-5 w-5 text-sky-300" data-id="element-217" />
                <span className="font-bold text-slate-200" data-id="element-218">{text}</span>
              </div>)}
          </div>

          <div className="mt-12 overflow-hidden rounded-[2rem] border border-white/10" data-id="element-219">
            <div className="grid grid-cols-3 bg-white/10 p-4 text-sm font-black text-slate-200" data-id="element-220">
              <div data-id="element-221">Need</div>
              <div className="text-center" data-id="element-222">Typical Provider</div>
              <div className="text-center text-sky-200" data-id="element-223">Watchdog</div>
            </div>
            {[['Local walkthrough', 'Sometimes', 'Yes'], ['Custom system design', 'Limited', 'Yes'], ['Commercial access control', 'Varies', 'Yes'], ['Repairs and upgrades', 'Varies', 'Yes'], ['Home and business support', 'Separate paths', 'One local team']].map(row => <div key={row[0]} className="grid grid-cols-3 border-t border-white/10 p-4 text-sm" data-id="element-224">
                <div className="font-bold text-white" data-id="element-225">{row[0]}</div>
                <div className="text-center text-slate-400" data-id="element-226">{row[1]}</div>
                <div className="text-center font-black text-sky-200" data-id="element-227">
                  {row[2]}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 lg:px-8" data-id="element-228">
        <h2 className="text-3xl font-black tracking-tight sm:text-4xl" data-id="element-229">
          Ready to see what your property needs?
        </h2>
        <p className="mx-auto mt-4 max-w-2xl leading-8 text-slate-600" data-id="element-230">
          A Watchdog specialist will walk your property, identify gaps, and put
          together a clear plan and quote.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-231">
          <CTAButton to="/assessment" data-id="element-232">Free commercial assessment</CTAButton>
          <CTAButton variant="dark" href="tel:+19168424729" data-id="element-233">
            Call (916) 842-4729
          </CTAButton>
        </div>
      </section>
    </>;
}